import { useEffect, useRef, useState } from "react";
import { ApiService } from "../../../../../api";
import CoverageDocumentService from "../../../../../api/services/operation/coverage-document-service";

const handleApiError = (err) => {
	if (!ApiService.isRequestCancellation(err?.message)) {
		console.error(err);
	}
};

const parseApiResponse = (data) => {
	const items = data?.content || data?.contents || [];
	const total = data?.totalElements ?? data?.total ?? 0;
	return { items, total };
};

export const useDeliverablesData = ({
	open,
	projectId,
	phaseId,
	selectedSourcePhaseId,
	searchLeft,
	searchRight,
	leftPage,
	leftPerPage,
	rightPage,
	rightPerPage,
}) => {
	const [leftDeliverables, setLeftDeliverables] = useState([]);
	const [rightDeliverables, setRightDeliverables] = useState([]);
	const [isLoadingLeft, setIsLoadingLeft] = useState(false);
	const [isLoadingRight, setIsLoadingRight] = useState(false);
	const [leftTotalCount, setLeftTotalCount] = useState(0);
	const [rightTotalCount, setRightTotalCount] = useState(0);
	const cancelTokenRef = useRef(null);
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		cancelTokenRef.current = ApiService.getCancelTokenSource();
		return () => {
			isMountedRef.current = false;
			if (cancelTokenRef.current) {
				ApiService.cancelTokens(cancelTokenRef.current);
			}
		};
	}, [open]);

	useEffect(() => {
		if (!open || !projectId || !phaseId) {
			return;
		}
		if (!cancelTokenRef.current) {
			cancelTokenRef.current = ApiService.getCancelTokenSource();
		}
		setIsLoadingRight(true);
		const filtersPayload = {
			phases: { is: true, matchAll: false, elements: [phaseId] },
		};

		if (searchRight.length > 2) {
			filtersPayload.name = searchRight;
		}
		CoverageDocumentService.getDeliverablesByCatFilter(
			{ projectId },
			filtersPayload,
			cancelTokenRef.current.token,
			{ page: rightPage, limit: rightPerPage }
		)
			.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				const { items, total } = parseApiResponse(data);
				setRightDeliverables(items);
				setRightTotalCount(total);
			})
			.catch(handleApiError)
			.finally(() => {
				if (isMountedRef.current) {
					setIsLoadingRight(false);
				}
			});
	}, [open, projectId, phaseId, rightPage, rightPerPage, searchRight]);

	useEffect(() => {
		if (!open || !projectId) {
			return;
		}
		if (!selectedSourcePhaseId) {
			if (isMountedRef.current) {
				setIsLoadingLeft(false);
				setLeftDeliverables([]);
			}
			return;
		}
		if (!cancelTokenRef.current) {
			cancelTokenRef.current = ApiService.getCancelTokenSource();
		}
		setIsLoadingLeft(true);
		const filtersPayload = {
			phases: { is: true, matchAll: false, elements: [selectedSourcePhaseId] },
		};

		if (searchLeft.length > 2) {
			filtersPayload.name = searchLeft;
		}
		CoverageDocumentService.getDeliverablesByCatFilter(
			{ projectId },
			filtersPayload,
			cancelTokenRef.current.token,
			{ page: leftPage, limit: leftPerPage }
		)
			.then((data) => {
				if (!isMountedRef.current) {
					return;
				}
				const { items, total } = parseApiResponse(data);
				setLeftDeliverables(items);
				setLeftTotalCount(total);
			})
			.catch(handleApiError)
			.finally(() => {
				if (isMountedRef.current) {
					setIsLoadingLeft(false);
				}
			});
	}, [leftPage, leftPerPage, open, projectId, selectedSourcePhaseId, searchLeft]);

	return {
		leftDeliverables,
		rightDeliverables,
		isLoadingLeft,
		isLoadingRight,
		leftTotalCount,
		rightTotalCount,
		cancelTokenRef,
		isMountedRef,
	};
};
